import { keyBy } from 'lodash';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { usePrevious } from 'react-use';

import AnalyticsPage from '@features/analytics/AnalyticsPage';
import GiftCardsPage from '@features/giftCards/GiftCardsPage';
import HomePage from '@features/home/HomePage';
import InventoryPage from '@features/inventory/InventoryPage';
import LiveViewPage from '@features/liveView/LiveViewPage';
import TransfersPage from '@features/transfers/TransfersPage';
import { useOrgSlug } from 'hooks/useOrgSlug';
import { useStores } from 'stores/rootStore';
import { PageIcon } from 'vectors';

export type NavigationEntity = {
  id: string;
  name: string;
  relativePath: string;
  absolutePath?: string;
  icon?: React.ReactNode;
  children: NavigationEntity[];
  component?: React.FC | React.ComponentClass;
};

const sidebarNavigationRoot: NavigationEntity = {
  id: 'root',
  name: 'Root',
  relativePath: '/',
  children: [
    {
      id: 'home',
      name: 'Home',
      icon: <PageIcon />,
      relativePath: '/',
      children: [],
      component: HomePage,
    },
    {
      id: 'inventory',
      name: 'Inventory',
      icon: <PageIcon />,
      relativePath: '/inventory',
      children: [
        {
          id: 'transfer',
          name: 'Transfers',
          relativePath: '/transfers',
          children: [],
          component: TransfersPage,
        },
      ],
      component: InventoryPage,
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: <PageIcon />,
      relativePath: '/analytics',
      component: AnalyticsPage,
      children: [
        {
          id: 'liveview',
          name: 'Live View',
          relativePath: '/liveview',
          children: [],
          component: LiveViewPage,
        },
      ],
    },
    {
      id: 'gift-cards',
      name: 'Gift Cards',
      icon: <PageIcon />,
      relativePath: '/gift-cards',
      children: [],
      component: GiftCardsPage,
    },
  ],
};

const marketplaceNavigationRoot: NavigationEntity = {
  id: 'root',
  name: 'Root',
  relativePath: '/',
  children: [
    {
      id: 'all',
      name: 'All',
      relativePath: '/',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'charge-stations',
      name: 'Charge stations',
      relativePath: '/charge-stations',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'powerbanks',
      name: 'Powerbanks',
      relativePath: '/powerbanks',
      icon: <PageIcon />,
      children: [],
    },
  ],
};

const settingsNavigationRoot: NavigationEntity = {
  id: 'root',
  name: 'Root',
  relativePath: '/settings',
  children: [
    {
      id: 'stores',
      name: 'Stores',
      relativePath: '/stores',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'my-account',
      name: 'My account',
      relativePath: '/my-account',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'preferences',
      name: 'Preferences',
      relativePath: '/preferences',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'billing',
      name: 'Billing',
      relativePath: '/billing',
      icon: <PageIcon />,
      children: [],
    },
    {
      id: 'permissions',
      name: 'Roles & permissions',
      relativePath: '/permissions',
      icon: <PageIcon />,
      children: [],
    },
  ],
};
const flattenNavigationEntities = (
  entities: NavigationEntity[],
  parentPath: string,
): NavigationEntity[] => {
  return entities.flatMap((entity) => {
    let absolutePath =
      parentPath === '/' ? entity.relativePath : `${parentPath}${entity.relativePath}`;
    if (entity.relativePath === '/') {
      absolutePath = parentPath;
    }
    return [
      {
        ...entity,
        absolutePath,
      },
      ...flattenNavigationEntities(entity.children, absolutePath),
    ];
  });
};

export function useIsSelected(entity: NavigationEntity) {
  const { selectedNavigationEntity } = useSidebarNavigationEntities();
  return entity.id === selectedNavigationEntity?.id;
}

export const useSidebarNavigationEntities = () => {
  const orgSlug = useOrgSlug();
  const basePath = orgSlug != null ? `/${orgSlug}` : '';
  const entities = useNavigationEntities(sidebarNavigationRoot, basePath);
  const previousSelectedEntity = usePrevious(entities.selectedNavigationEntity);
  const { isOpen } = useSettingsNavigationEntities();

  return useMemo(
    () => ({
      ...entities,
      selectedNavigationEntity: isOpen ? previousSelectedEntity : entities.selectedNavigationEntity,
    }),
    [entities, previousSelectedEntity, isOpen],
  );
};

export const useMarketplaceNavigationEntities = () => {
  const basePath = '/marketplace';
  return useNavigationEntities(marketplaceNavigationRoot, basePath);
};

export const useIsMarketplaceNavidationSelected = (entity: NavigationEntity) => {
  const { selectedNavigationEntity } = useMarketplaceNavigationEntities();
  return selectedNavigationEntity?.id === entity.id;
};

export const useSettingsNavigationEntities = () => {
  const orgSlug = useOrgSlug();
  const basePath = orgSlug != null ? `/${orgSlug}/settings` : '/settings';
  const entities = useNavigationEntities(settingsNavigationRoot, basePath, {
    pushOnMount: false,
  });
  const pathname = usePathname();
  const isOpen = pathname.startsWith(basePath);
  const { pageStore } = useStores();
  const router = useRouter();

  const closeSettings = useCallback(() => {
    if (pageStore.sidebarPath != null) {
      router.replace(pageStore.sidebarPath);
    } else {
      router.replace(`/${orgSlug}`);
    }
  }, [pageStore.sidebarPath, router, orgSlug]);

  return useMemo(
    () => ({
      ...entities,
      basePath,
      isOpen,
      closeSettings,
    }),
    [entities, basePath, isOpen, closeSettings],
  );
};

export const useIsSettingsNavigationSelected = (entity: NavigationEntity) => {
  const { selectedNavigationEntity } = useSettingsNavigationEntities();
  return selectedNavigationEntity?.id === entity.id;
};

type Options = {
  pushOnMount?: boolean;
};
const useNavigationEntities = (root: NavigationEntity, basePath: string, options?: Options) => {
  const pathname = usePathname();
  const router = useRouter();
  const { pushOnMount = true } = options ?? {};

  const flattenedEntities = useMemo(
    () => flattenNavigationEntities(root.children, basePath),
    [root, basePath],
  );

  const selectedNavigationEntity = useMemo(() => {
    const matchingEntities = flattenedEntities.filter((entity) => {
      return entity.absolutePath != null && pathname.startsWith(entity.absolutePath);
    });
    // sorted by length of absolute path in descending order.
    // The assumption here is that the longest absolute path is the most specific.
    const sortedEntities = matchingEntities.sort(
      (a, b) => (b.absolutePath?.length ?? 0) - (a.absolutePath?.length ?? 0),
    );
    return sortedEntities[0];
  }, [flattenedEntities, pathname]);

  useEffect(() => {
    if (!pushOnMount) {
      return;
    }

    const defaultPath = flattenedEntities[0].absolutePath;
    if (selectedNavigationEntity == null && defaultPath != null) {
      router.push(defaultPath);
    }
  }, [selectedNavigationEntity, flattenedEntities, router, pushOnMount]);

  return {
    root,
    all: flattenedEntities,
    byId: keyBy(flattenedEntities, 'id'),
    selectedNavigationEntity,
  };
};
